import React, { useRef, useEffect, useState } from 'react';
import { useNotificaciones } from 'contexts/NotificacionContext';
import { NotificacionMia, CategoriaNotificacion } from 'types/notificacion.types';

// ─────────────────────────────────────────────────────────────────
// CampanaNotificaciones
// Ícono de campana en la navbar con badge + dropdown de recientes.
// Badge rojo: no leídas | Badge ámbar: comunicados sin confirmar.
// ─────────────────────────────────────────────────────────────────

interface Props {
  onVerTodas: () => void;
}

const CATEGORIA_DOT: Record<CategoriaNotificacion, string> = {
  COMUNICADO:   'bg-blue-500',
  ALERTA:       'bg-red-500',
  RECORDATORIO: 'bg-amber-500',
};

const fmtRelativo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'Ahora';
  if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h} h`;
  const d = Math.floor(h / 24);
  return `Hace ${d} día${d > 1 ? 's' : ''}`;
};

const NotifRow: React.FC<{
  n:        NotificacionMia;
  onMarcar: (id: number) => void;
}> = ({ n, onMarcar }) => (
  <button
    onClick={() => { if (!n.leida) onMarcar(n.idNotificacion); }}
    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors
                border-b border-slate-100 last:border-0
                ${!n.leida ? 'bg-blue-50/40' : ''}`}
  >
    <div className="flex items-start gap-2.5">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5
        ${!n.leida ? (CATEGORIA_DOT[n.categoria] ?? 'bg-blue-500') : 'bg-transparent'}`}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className={`text-sm truncate ${n.leida ? 'text-slate-600' : 'font-semibold text-slate-800'}`}>
            {n.titulo}
          </p>
          {n.requiereConfirmacion && !n.confirmada && (
            <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide
                             bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              Confirmar
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 truncate"
          dangerouslySetInnerHTML={{ __html: n.mensaje.replace(/<[^>]*>/g, ' ').trim() }}/>
        <p className="text-[10px] text-slate-400 mt-0.5">{fmtRelativo(n.fechaCreacion)}</p>
      </div>
    </div>
  </button>
);

const CampanaNotificaciones: React.FC<Props> = ({ onVerTodas }) => {
  const { notificaciones, noLeidas, sinConfirmar, marcarLeida, marcarTodasLeidas } = useNotificaciones();
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const recientes = notificaciones.slice(0, 6);
  const totalBadge = noLeidas + sinConfirmar;

  return (
    <div ref={ref} className="relative">
      {/* Botón campana */}
      <button
        onClick={() => setAbierto(prev => !prev)}
        className="relative p-2 rounded-xl text-slate-500 hover:text-blue-700
                   hover:bg-slate-100 transition-colors"
        title="Notificaciones"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth={1.5} className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"/>
        </svg>

        {/* Badge principal — no leídas (rojo) */}
        {noLeidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full
                           bg-red-500 text-white text-[10px] font-bold
                           flex items-center justify-center px-1 leading-none">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        )}

        {/* Badge secundario — comunicados sin confirmar (ámbar), cuando no hay no leídas */}
        {noLeidas === 0 && sinConfirmar > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full
                           bg-amber-500 text-white text-[10px] font-bold
                           flex items-center justify-center px-1 leading-none">
            {sinConfirmar}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {abierto && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl
                        border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <p className="font-bold text-slate-800 text-sm">Notificaciones</p>
              {noLeidas > 0 && (
                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {noLeidas} nueva{noLeidas > 1 ? 's' : ''}
                </span>
              )}
              {sinConfirmar > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {sinConfirmar} sin confirmar
                </span>
              )}
            </div>
            {noLeidas > 0 && (
              <button
                onClick={() => marcarTodasLeidas()}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold transition-colors"
              >
                Marcar todas
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-80 overflow-y-auto">
            {recientes.length === 0 ? (
              <div className="py-10 text-center text-slate-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth={1.5} className="w-8 h-8 mx-auto mb-2 text-slate-300">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"/>
                </svg>
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              recientes.map(n => (
                <NotifRow key={n.idNotificacion} n={n} onMarcar={marcarLeida}/>
              ))
            )}
          </div>

          {/* Footer */}
          {sinConfirmar > 0 && (
            <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700 font-semibold">
              Tienes {sinConfirmar} comunicado{sinConfirmar > 1 ? 's' : ''} pendiente{sinConfirmar > 1 ? 's' : ''} de confirmar
            </div>
          )}
          <button
            onClick={() => { setAbierto(false); onVerTodas(); }}
            className="w-full px-4 py-3 text-sm font-semibold text-blue-700
                       hover:bg-blue-50 transition-colors border-t border-slate-100 text-center"
          >
            Ver todas las notificaciones
          </button>
        </div>
      )}
    </div>
  );
};

export default CampanaNotificaciones;
